import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Icon } from "../components/Icon";
import { Page, TopBar } from "../components/Layout";
import { LoadMore } from "../components/ListControls";
import { SearchableSelect } from "../components/SearchableSelect";
import { api, apiBase, setApiBase } from "../lib/api";
import { onEventsStatus } from "../lib/events";
import { installedAll, onInstalledChange, opensLocally, syncBundles, type SyncResult } from "../lib/installer";
import { notificationPermission, type PhonePermission } from "../lib/notify";
import { isNative, openExternal, platform } from "../lib/platform";
import { load, save } from "../lib/storage";
import { useTheme, type ThemePref } from "../lib/theme";
import { useToast } from "../lib/toast";
import { formatPrice, usd, type AIOverview, type AppCard, type Subscription } from "../lib/types";
import { absoluteUrl, installedVersion, installRelease, latestRelease, sizeLabel, type Installed, type Release } from "../lib/updates";
import { usePaged } from "../lib/usePaged";
import { versionLabel } from "../version";

const SECTIONS: Record<string, { title: string; body: () => JSX.Element | null }> = {
  notifications: { title: "Notifications", body: NotificationSettings },
  app: { title: "Omni app", body: AppUpdates },
  subscriptions: { title: "Subscriptions", body: Subscriptions },
  ai: { title: "AI usage", body: AIUsage },
  appearance: { title: "Appearance", body: Appearance },
  security: { title: "Password", body: PasswordForm },
  data: { title: "Your data", body: YourData },
  device: { title: "On this device", body: DeviceApps },
  developer: { title: "Developer", body: DeveloperSettings },
};

export function Settings() {
  const { section = "appearance" } = useParams();
  const nav = useNavigate();
  const s = SECTIONS[section] ?? SECTIONS.appearance;
  const Body = s.body;
  return (
    <>
      <TopBar
        title={s.title}
        avatar={false}
        back={<button className="icon-btn" aria-label="Back to profile" onClick={() => nav("/profile")}><Icon name="chevron-left" /></button>}
      />
      <Page>
        <Body />
      </Page>
    </>
  );
}

function Appearance() {
  const { pref, setPref } = useTheme();
  const options: { value: ThemePref; label: string; icon: string }[] = [
    { value: "light", label: "Light", icon: "sun" },
    { value: "dark", label: "Dark", icon: "moon" },
    { value: "system", label: "System", icon: "layout" },
  ];
  return (
    <>
      <div className="segmented" role="radiogroup" aria-label="Theme">
        {options.map((o) => (
          <button key={o.value} role="radio" aria-checked={pref === o.value} className={pref === o.value ? "active" : ""} onClick={() => setPref(o.value)}>
            <Icon name={o.icon} size={18} />{o.label}
          </button>
        ))}
      </div>
      <p className="small muted">System follows your device. Apps receive the same light or dark signal.</p>
      <Row label="Server" value={apiBase()} to="/settings/server" />
    </>
  );
}

function Row({ label, value, to }: { label: string; value: string; to: string }) {
  const nav = useNavigate();
  return (
    <div className="card flush">
      <button className="list-row" onClick={() => nav(to)}>
        <Icon name="server" size={20} style={{ color: "var(--omni-text-muted)" }} />
        <span className="grow">{label}<span className="sub m">{value}</span></span>
        <Icon name="chevron-right" size={20} className="trail" />
      </button>
    </div>
  );
}

function Subscriptions() {
  const toast = useToast();
  const subs = usePaged<Subscription>("/api/billing/subscriptions", { sort: "-created_at", limit: 20 });
  const act = async (s: Subscription, action: "cancel" | "resume") => {
    await api(`/api/billing/subscriptions/${s.id}/${action}`, { method: "POST" });
    toast(action === "cancel" ? "Subscription will end at the period end" : "Subscription resumed");
    subs.reload();
  };
  const tone = (s: Subscription) => (["active", "trialing"].includes(s.status) ? (s.cancel_at_period_end ? "warn" : "ok") : "");
  return (
    <>
      {subs.total === 0 && !subs.loading && <p className="muted">No subscriptions yet. Paid apps from the marketplace show up here.</p>}
      {subs.items.length > 0 && (
        <div className="card flush">
          {subs.items.map((s) => (
            <div key={s.id} className="list-row" style={{ minHeight: 72 }}>
              <span className="grow">
                <strong style={{ fontWeight: 600 }}>{s.app_name}</strong> <span className="muted">· {s.plan_id}</span>
                <span className="sub">
                  {s.provider === "grant" ? (
                    <>Given by an administrator{s.current_period_end ? ` · until ${new Date(new Date(s.current_period_end).getTime() - 86_400_000).toLocaleDateString()}` : ""}</>
                  ) : (
                    <>
                      {formatPrice(s.price, s.currency, s.interval)}
                      {s.list_price != null && s.list_price > s.price && <> <s className="price-was">{formatPrice(s.list_price, s.currency)}</s></>}
                      {s.current_period_end && ` · ${s.cancel_at_period_end ? "ends" : "renews"} ${new Date(s.current_period_end).toLocaleDateString()}`}
                    </>
                  )}
                </span>
              </span>
              <span className={`pill ${tone(s)}`}>{s.provider === "grant" && s.status === "active" ? "Granted" : s.status === "trialing" ? "Trial" : s.status}</span>
              {["active", "trialing"].includes(s.status) && s.provider !== "grant" &&
                (s.cancel_at_period_end ? (
                  <button className="btn btn-sm" onClick={() => act(s, "resume")}>Resume</button>
                ) : (
                  <button className="btn btn-sm btn-ghost danger" onClick={() => act(s, "cancel")}>Cancel</button>
                ))}
            </div>
          ))}
        </div>
      )}
      <LoadMore hasMore={subs.hasMore} loading={subs.loading} onClick={subs.loadMore} shown={subs.items.length} total={subs.total} />
    </>
  );
}

/** The user's AI allowance this month (every call goes through the AI gateway). */
function AIUsage() {
  const [data, setData] = useState<AIOverview | null>(null);
  useEffect(() => {
    api<AIOverview>("/api/ai/budget").then(setData).catch(() => setData(null));
  }, []);
  if (!data) return null;
  const monthly = data.limits.filter((l) => l.period === "month" && l.scope === "user");
  return (
    <>
      <Link to="/marketplace/omni" className="omni-banner">
        <span className="ic-wrap"><Icon name="sparkle" size={24} /></span>
        <span className="grow">
          <strong>{data.plan ? `Omni plan: ${data.plan.name}` : "No Omni plan"}</strong>
          <span className="sub">{data.plan ? "See what's included or change it." : data.plan_required ? "AI features come with an Omni plan. Choose one." : "Get more AI with an Omni plan."}</span>
        </span>
        <Icon name="chevron-right" size={20} />
      </Link>
      <div className="card stack">
        {data.plan_required && <p className="small muted">No AI until you choose an Omni plan · {usd(data.spent_usd)} used this month</p>}
        {monthly.length === 0 && !data.plan_required && <p className="small muted">No limit on your plan · {usd(data.spent_usd)} used this month</p>}
        {monthly.map((l, i) => (
          <Meter key={i} label="Your allowance this month" pct={l.used_pct} detail={`${usd(l.spent_usd)} of ${usd(l.limit_usd)}`} />
        ))}
        {!data.allowed && !data.plan_required && <p className="error small">{data.reason}</p>}
      </div>
      {data.apps.length > 0 && (
        <>
          <span className="lbl">By app</span>
          <div className="card stack">
            <ul className="usage-list">
              {data.apps.map((a) =>
                a.limit ? (
                  <li key={a.app_id ?? "omni"}>
                    <Meter label={`${a.app_id} · ${a.requests} requests`} pct={a.limit.used_pct} detail={`${usd(a.limit.spent_usd)} of ${usd(a.limit.limit_usd)}`} />
                  </li>
                ) : (
                  <li key={a.app_id ?? "omni"} className="row">
                    <span className="grow">{a.app_id ?? "Omni"}</span>
                    <span className="small muted">{a.requests} requests</span>
                    <span className="small num">{usd(a.spent_usd)}</span>
                  </li>
                ),
              )}
            </ul>
          </div>
        </>
      )}
      <p className="small muted">Tier: {data.tier}. Resets on the 1st of each month.</p>
    </>
  );
}

export function Meter({ label, pct, detail }: { label: string; pct: number; detail: string }) {
  const clamped = Math.min(pct, 100);
  return (
    <div className="meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(clamped)} aria-label={label}>
      <div className="row">
        <span className="grow small">{label}</span>
        <span className="small muted num">{detail}</span>
      </div>
      <div className="meter-track">
        <div className={`meter-fill${pct >= 100 ? " full" : pct >= 80 ? " warn" : ""}`} style={{ width: `${clamped}%` }} />
      </div>
    </div>
  );
}

function PasswordForm() {
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api("/api/me/password", { body: { current_password: current, new_password: next } });
      setCurrent("");
      setNext("");
      toast("Password changed", "success");
    } catch (err) {
      toast((err as Error).message, "error");
    }
  };
  return (
    <form className="stack" onSubmit={submit}>
      <label className="field"><span className="field-label">Current password</span><input className="input" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required /></label>
      <label className="field"><span className="field-label">New password</span><input className="input" type="password" autoComplete="new-password" minLength={8} maxLength={128} value={next} onChange={(e) => setNext(e.target.value)} required /><span className="xs muted">8 or more characters</span></label>
      <button className="btn btn-primary btn-block">Change password</button>
    </form>
  );
}

function kb(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** ADR-0024/0025: the apps installed on this device, and the live connection. */
function DeviceApps() {
  const toast = useToast();
  const [apps, setApps] = useState(installedAll);
  const [live, setLive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState(opensLocally);
  useEffect(() => onInstalledChange(() => setApps(installedAll())), []);
  useEffect(() => onEventsStatus(setLive), []);
  const check = async () => {
    setBusy(true);
    try {
      const r: SyncResult = await syncBundles();
      const parts = [r.updated.length && `${r.updated.length} updated`, r.removed.length && `${r.removed.length} removed`, r.failed.length && `${r.failed.length} failed`].filter(Boolean);
      toast(parts.length ? `Apps: ${parts.join(", ")}` : "Everything is up to date", r.failed.length ? "error" : "success");
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <p className="small muted">
        {isNative || local
          ? "Installed apps are kept on this device and open without waiting for the network. Updates download in the background and apply the next time you open the app."
          : "Installed apps are kept in this browser's cache and open without downloading again. Updates download in the background."}
      </p>
      <div className="card flush">
        {apps.map((a) => (
          <div key={a.app_id} className="list-row">
            <span className="grow">
              {a.name}
              <span className="sub">Version {a.version} · {kb(a.size)}{a.installed_at ? ` · updated ${new Date(a.installed_at).toLocaleDateString()}` : ""}</span>
            </span>
            <code className="xs muted">{a.hash.slice(0, 7)}</code>
          </div>
        ))}
        {apps.length === 0 && <div className="list-row muted">No apps on this device yet.</div>}
      </div>
      <button className="btn btn-block" disabled={busy} onClick={check}><Icon name="rotate" size={20} />{busy ? "Checking…" : "Check for updates"}</button>
      <div className="card row">
        <span className={`pill ${live ? "ok" : "warn"}`}>{live ? "Live" : "Offline"}</span>
        <span className="small muted grow">{live ? "Connected: apps and widgets update as things happen." : "Not connected. Changes show the next time you open an app."}</span>
      </div>
      {/* Development only: the web's Content-Security-Policy blocks the inlined copy in production */}
      {!isNative && import.meta.env.DEV && (
        <label className="card row small">
          <input type="checkbox" className="switch" checked={local} onChange={(e) => { setLocal(e.target.checked); save("omni.localApps", e.target.checked); void syncBundles().catch(() => undefined); }} />
          <span className="grow">Open apps from this browser's copy, as the mobile app does (for testing)</span>
        </label>
      )}
    </>
  );
}

/** Point an installed app at a local dev server; no rebuild of anything. */
function DeveloperSettings() {
  const apps = usePaged<AppCard>("/api/apps", { sort: "name", limit: 50 });
  const [overrides, setOverrides] = useState(() => load<Record<string, string>>("omni.devOverrides", {}));
  const [appId, setAppId] = useState<string | null>(null);
  const [url, setUrl] = useState("http://localhost:5174/");
  const update = (next: Record<string, string>) => {
    setOverrides(next);
    save("omni.devOverrides", next);
  };
  return (
    <div className="card stack">
      <p className="small muted">Load an installed app from your local dev server instead of the published bundle.</p>
      {/* ADR-0009: installed apps are user data, so the picker is searchable with server-side search. */}
      <SearchableSelect label="App" value={appId} onChange={setAppId} onSearch={apps.setQ} options={apps.items.map((a) => ({ value: a.id, label: a.name, hint: a.id }))} />
      <label className="field"><span className="field-label">Dev server URL</span><input className="input m" type="url" value={url} onChange={(e) => setUrl(e.target.value)} /></label>
      <button className="btn" disabled={!appId} onClick={() => appId && update({ ...overrides, [appId]: url })}>Use dev URL</button>
      {Object.entries(overrides).map(([id, u]) => (
        <div key={id} className="row small">
          <code className="grow">{id} → {u}</code>
          <button className="btn btn-sm btn-ghost" onClick={() => { const n = { ...overrides }; delete n[id]; update(n); }}>Remove</button>
        </div>
      ))}
    </div>
  );
}

export function ServerSettings() {
  const nav = useNavigate();
  const [url, setUrl] = useState(apiBase());
  const [status, setStatus] = useState<string | null>(null);
  const test = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch(`${url.replace(/\/$/, "")}/api/health`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setApiBase(url);
      setStatus("Connected");
      setTimeout(() => nav(-1), 600);
    } catch (err) {
      setStatus(`Could not reach server: ${(err as Error).message}`);
    }
  };
  return (
    <div className="auth">
      <form className="stack" onSubmit={test}>
        <h1 style={{ fontSize: 28 }}>Omni server</h1>
        <p className="lede">Connect the app to your own Omni server.</p>
        <input className="input m" type="url" value={url} onChange={(e) => setUrl(e.target.value)} required aria-label="Server URL" />
        {status && <p className="small" role="status">{status}</p>}
        <div className="grid-2">
          <button className="btn btn-primary">Save</button>
          <button type="button" className="btn" onClick={() => { setApiBase(null); setUrl(apiBase()); }}>Reset</button>
        </div>
        <button type="button" className="btn btn-ghost" onClick={() => nav(-1)}>Back</button>
      </form>
    </div>
  );
}

export function SettingsSection({ children }: { children: ReactNode }) {
  return <div className="stack">{children}</div>;
}

/** ADR-0022: what each app stores about you; download it or delete it. */
function YourData() {
  const toast = useToast();
  const [items, setItems] = useState<{ app_id: string; name: string; installed: boolean; records: number; files: number }[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = () => api<{ items: NonNullable<typeof items> }>("/api/me/data").then((r) => setItems(r.items));
  useEffect(() => {
    load().catch((e) => toast((e as Error).message, "error"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportData = async (appId: string) => {
    setBusy(appId);
    try {
      // A short-lived link, so the download also works in the system browser on mobile.
      const { url } = await api<{ url: string }>(`/api/me/data/${appId}/export-link`, { method: "POST" });
      await openExternal(url);
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(null);
    }
  };
  const remove = async (appId: string, name: string) => {
    if (!confirm(`Delete everything ${name} stores about you? This can't be undone. The app stays installed.`)) return;
    setBusy(appId);
    try {
      const r = await api<{ records: number; files: number }>(`/api/me/data/${appId}`, { method: "DELETE" });
      toast(`Deleted ${r.records} records and ${r.files} files`, "success");
      await load();
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <p className="muted" style={{ lineHeight: 1.5 }}>Each app keeps your data separately from the others. Download a copy any time, or delete it.</p>
      {items === null && <p className="muted small">Loading…</p>}
      {items?.length === 0 && <p className="muted">No app keeps data about you yet.</p>}
      {items && items.length > 0 && (
        <div className="card flush">
          {items.map((a) => (
            <div key={a.app_id} className="list-row" style={{ minHeight: 72 }}>
              <span className="grow">
                <strong style={{ fontWeight: 600 }}>{a.name}</strong>
                <span className="sub">{a.records} records{a.files ? ` · ${a.files} files` : ""}{a.installed ? "" : " · not installed"}</span>
              </span>
              <button className="btn btn-sm" disabled={busy === a.app_id || !(a.records || a.files)} onClick={() => exportData(a.app_id)}><Icon name="share" size={18} />Download</button>
              <button className="btn btn-sm btn-ghost danger" aria-label={`Delete my ${a.name} data`} disabled={busy === a.app_id || !(a.records || a.files)} onClick={() => remove(a.app_id, a.name)}><Icon name="trash" size={18} /></button>
            </div>
          ))}
        </div>
      )}
      <p className="xs muted">Downloads are a .ndjson.gz file: one JSON record per line, readable by any tool.</p>
    </>
  );
}

interface NotifySource { source: string; name: string; app_id: string | null; muted: boolean; allowed?: boolean }

/** ADR-0031: which sources may notify you, and whether this device may show them. */
function NotificationSettings() {
  const toast = useToast();
  const [sources, setSources] = useState<NotifySource[] | null>(null);
  const [perm, setPerm] = useState<PhonePermission | null>(null);
  useEffect(() => {
    api<{ sources: NotifySource[] }>("/api/notifications/settings").then((r) => setSources(r.sources)).catch((e) => toast((e as Error).message, "error"));
    void notificationPermission().then(setPerm).catch(() => setPerm("unavailable"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (s: NotifySource, on: boolean) => {
    setSources((all) => all?.map((x) => (x.source === s.source ? { ...x, muted: !on } : x)) ?? null);
    try {
      const r = await api<{ sources: NotifySource[] }>("/api/notifications/settings", { method: "PUT", body: { source: s.source, muted: !on } });
      setSources(r.sources);
    } catch (e) {
      toast((e as Error).message, "error");
    }
  };
  const ask = async () => {
    const p = await notificationPermission(true);
    setPerm(p);
    if (p === "denied") toast(isNative ? "Notifications are blocked for Omni. Allow them in Android's settings." : "Notifications are blocked for Omni in this browser.", "error");
  };
  const omni = sources?.filter((s) => !s.app_id) ?? [];
  const apps = sources?.filter((s) => s.app_id) ?? [];

  return (
    <>
      <div className="card row">
        <Icon name={perm === "granted" ? "bell" : "bell-off"} size={20} style={{ color: "var(--omni-text-muted)" }} />
        <span className="grow small">
          {perm === "granted" ? (isNative ? "This phone shows notifications, also when Omni is closed." : "This browser shows notifications while Omni is open in a tab.")
            : perm === "unavailable" ? "This browser can't show notifications. You'll find them under the bell."
              : perm === "denied" ? (isNative ? "Blocked in Android's settings. You'll still find them under the bell." : "Blocked in this browser. You'll still find them under the bell.")
                : isNative ? "Let this phone show notifications, also when Omni is closed." : "Let this browser show notifications while Omni is open."}
        </span>
        {perm === "prompt" && <button className="btn btn-sm btn-primary" onClick={ask}>Allow</button>}
      </div>

      <span className="lbl">From Omni</span>
      <div className="card flush">
        {omni.map((s) => (
          <label key={s.source} className="list-row" style={{ cursor: "pointer" }}>
            <span className="grow">{s.name}</span>
            <input type="checkbox" className="switch" checked={!s.muted} onChange={(e) => toggle(s, e.target.checked)} aria-label={s.name} />
          </label>
        ))}
      </div>

      {apps.length > 0 && (
        <>
          <span className="lbl">From your apps</span>
          <div className="card flush">
            {apps.map((s) => (
              <label key={s.source} className="list-row" style={{ cursor: "pointer" }}>
                <span className="grow">{s.name}{s.allowed === false && <span className="sub">Not allowed yet: Omni asks you when you next open the app</span>}</span>
                <input type="checkbox" className="switch" checked={!s.muted} onChange={(e) => toggle(s, e.target.checked)} aria-label={s.name} />
              </label>
            ))}
          </div>
        </>
      )}
      <p className="small muted">Turned off: nothing from it reaches your inbox, this device or your pipelines.</p>
    </>
  );
}

/** ADR-0032: this app's version, what's new, and installing a newer one. */
function AppUpdates() {
  const toast = useToast();
  const [mine, setMine] = useState<Installed | null>(null);
  const [latest, setLatest] = useState<Release | null | undefined>(undefined);
  const [pct, setPct] = useState<number | null>(null);
  const history = usePaged<Release>("/api/app/releases", { sort: "-build", limit: 10, extra: { platform: "android" } });

  const check = () => {
    setLatest(undefined);
    latestRelease().then(setLatest).catch((e) => { setLatest(null); toast((e as Error).message, "error"); });
  };
  useEffect(() => {
    void installedVersion().then(setMine);
    check();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const install = async (r: Release) => {
    setPct(0);
    try {
      await installRelease(r, setPct);
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setPct(null);
    }
  };
  const android = isNative && platform === "android";

  return (
    <>
      <div className="card stack">
        <div className="row">
          <span className="grow">
            <strong style={{ fontWeight: 600 }}>{android ? "Installed" : "Web app"}</strong>
            <span className="sub">{mine ? `${mine.version} (build ${mine.build})` : versionLabel()}</span>
          </span>
          {android && latest && !latest.update && <span className="pill ok">Up to date</span>}
          {android && latest?.update && <span className={`pill ${latest.required ? "bad" : "warn"}`}>{latest.required ? "Update required" : "Update available"}</span>}
        </div>
        {latest === undefined && <p className="small muted">Checking for updates…</p>}
        {latest === null && <p className="small muted">No phone app has been published on this server yet.</p>}
        {latest && (android ? latest.update : true) && (
          <>
            <div className="row">
              <span className="grow">
                <strong style={{ fontWeight: 600 }}>Omni {latest.version}</strong>
                <span className="sub">Build {latest.build} · {sizeLabel(latest.size)}{latest.published_at ? ` · ${new Date(latest.published_at).toLocaleDateString()}` : ""}</span>
              </span>
            </div>
            {latest.notes && <p className="update-notes">{latest.notes}</p>}
            {android ? (
              <button className="btn btn-primary btn-block" disabled={pct !== null} onClick={() => install(latest)}>
                <Icon name="download" size={20} />{pct !== null ? `Downloading… ${pct}%` : `Install ${latest.version}`}
              </button>
            ) : (
              <a className="btn btn-primary btn-block" href={absoluteUrl(latest.download_url)} download={latest.filename}>
                <Icon name="download" size={20} />Download for Android
              </a>
            )}
          </>
        )}
        {android && <button className="btn btn-block" onClick={check}><Icon name="rotate" size={20} />Check for updates</button>}
      </div>
      {android && <p className="xs muted">Android asks you to confirm the install. The first time, it asks you to allow Omni to install apps.</p>}
      {!android && latest && <p className="xs muted">On your phone, open this page in the browser and tap Download. Android asks you to allow installing it the first time.</p>}

      {history.items.length > 1 && (
        <>
          <span className="lbl">Earlier versions</span>
          <div className="card flush">
            {history.items.filter((r) => r.id !== latest?.id).map((r) => (
              <div key={r.id} className="list-row" style={{ alignItems: "flex-start" }}>
                <span className="grow">
                  {r.version} <span className="muted small">· build {r.build}</span>
                  {r.notes && <span className="sub" style={{ whiteSpace: "pre-wrap" }}>{r.notes}</span>}
                </span>
                <span className="xs muted">{r.published_at ? new Date(r.published_at).toLocaleDateString() : ""}</span>
              </div>
            ))}
          </div>
          <LoadMore hasMore={history.hasMore} loading={history.loading} onClick={history.loadMore} shown={history.items.length} total={history.total} />
        </>
      )}
    </>
  );
}
